import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useLocation } from "react-router-dom";
import { Apple } from "lucide-react";
import { useHeader } from "../../../Providers/HeaderContextProvider";
import useCorporateBranding from "../hooks/useCorporateBranding";
import CorporateHeader from "../components/CorporateHeader";
import CorporateLoadingShell from "../components/CorporateLoadingShell";
import uatQRCode from "../../../Assets/icon/uat-aris-app-code.png";
import "../Activation/style.css";

const APP_STORE_URL = "https://apps.apple.com/app/aris";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.aris";

const fireConfetti = () => {
  try {
    const fn = typeof window !== "undefined" ? window.confetti : null;
    if (typeof fn === "function") {
      fn({ particleCount: 90, spread: 70, origin: { y: 0.4 } });
    }
  } catch (_) {
    /* ignore */
  }
};

const getQRCodeImage = () => {
  const isProduction =
    process.env.REACT_APP_ENV === "production" ||
    process.env.NODE_ENV === "production";
  if (isProduction && process.env.REACT_APP_PROD_QR_CODE) {
    return process.env.REACT_APP_PROD_QR_CODE;
  }
  return uatQRCode;
};

const AndroidGlyph = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.523 15.34a1.04 1.04 0 1 1 0-2.08 1.04 1.04 0 0 1 0 2.08m-11.046 0a1.04 1.04 0 1 1 0-2.08 1.04 1.04 0 0 1 0 2.08m11.42-6.02 2.077-3.6a.43.43 0 0 0-.158-.59.434.434 0 0 0-.59.158l-2.105 3.65a13.07 13.07 0 0 0-5.121-1.014c-1.82 0-3.547.36-5.121 1.014L4.774 5.288a.434.434 0 0 0-.59-.158.43.43 0 0 0-.158.59l2.077 3.6C2.737 11.166.65 14.31.32 18h23.36c-.33-3.69-2.417-6.834-5.783-8.68" />
  </svg>
);

const DonePage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { setHeaderContent } = useHeader();
  const { branding, content, loading, notFound } =
    useCorporateBranding(slug, "activation");

  const [activeTab, setActiveTab] = useState("ios");

  useEffect(() => {
    setHeaderContent?.(null);
  }, [setHeaderContent]);

  useEffect(() => {
    if (loading || notFound) return undefined;
    fireConfetti();
    return undefined;
  }, [loading, notFound]);

  const sections = content?.sections || {};
  const themeStyle = useMemo(
    () => ({
      "--corporate-primary": branding?.primary_color || "#4377fc",
      "--corporate-secondary": branding?.secondary_color || "#0d5eaf",
      "--corporate-font-scale":
        typeof branding?.content_font_scale === "number"
          ? branding.content_font_scale
          : 1,
    }),
    [branding]
  );

  if (notFound) return null;
  if (loading)
    return <CorporateLoadingShell branding={branding} themeStyle={themeStyle} />;

  const firstName = location.state?.first_name || "";
  const displayName = branding?.display_name || branding?.name || "";

  const sponsorLine =
    sections.footer?.sponsor_line ||
    (displayName ? `Sponsored by ${displayName}` : "");
  const copyrightLine = sections.footer?.copyright_line || "© 2026 Aris";

  return (
    <div
      className="corp-activation min-h-screen flex flex-col"
      style={themeStyle}
    >
      <Helmet>
        <title>Welcome to Aris — {displayName}</title>
        <meta name="robots" content="noindex" />
        <link
          href="https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,400;0,500;0,600;0,700;1,500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <CorporateHeader branding={branding} />

      <main className="flex-1 flex flex-col items-center px-4 md:px-9 pt-10 md:pt-12 pb-12">
        <div className="w-full max-w-[584px]">
          <div className="corp-cta-card-shadow">
            <div className="corp-cta-card-header" style={{ padding: "24px 16px" }}>
              <div className="flex flex-col gap-3 text-white">
                <h1 className="text-2xl font-semibold m-0 leading-tight">
                  {firstName ? `Get the app, ${firstName}` : "Get the app"}
                </h1>
                <p className="text-sm m-0 leading-relaxed text-white/95">
                  Scan the code or tap the button to open the app in your app
                  store.
                </p>
              </div>
              <div className="self-start inline-flex items-center gap-1.5 rounded-full border border-solid border-white/20 bg-white/10 px-4 py-1">
                <span
                  aria-hidden="true"
                  className="block size-2.5 rounded-full bg-[#04d9b5] border-solid border-[1.25px] border-[#b1f3e8]"
                />
                <span className="text-xs font-medium text-white">
                  Ready to Install
                </span>
              </div>
            </div>

            <div className="bg-white border border-solid border-[#e4e4e7] border-t-0 rounded-b-[20px] px-4 pt-6 pb-9 flex flex-col items-center gap-9">
              {/* iOS / Android tabs */}
              <div
                role="tablist"
                aria-label="Choose your platform"
                className="bg-[#f7f7f7] border border-solid border-white/20 rounded-2xl p-0.5 flex h-[42px] w-full max-w-[358px]"
              >
                {[
                  { id: "ios", label: "iOS" },
                  { id: "android", label: "Android" },
                ].map((tab) => {
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 min-w-[72px] px-1 py-2 rounded-xl text-base text-black transition-all cursor-pointer ${
                        active
                          ? "bg-white border border-solid border-[#e4e4e7] font-semibold shadow-[0_0_7.5px_rgba(0,0,0,0.1)]"
                          : "bg-transparent border-0 font-medium"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* QR + label */}
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-1.5">
                  {activeTab === "ios" ? (
                    <Apple size={16} className="text-black" />
                  ) : (
                    <span className="text-black">
                      <AndroidGlyph size={16} />
                    </span>
                  )}
                  <span className="text-base font-medium text-black">
                    {activeTab === "ios" ? "App Store" : "Google Play"}
                  </span>
                </div>
                <div className="border border-dashed border-[#7e7f83] rounded-xl size-[159px] overflow-hidden bg-white">
                  <img
                    src={getQRCodeImage()}
                    alt="QR code to open Aris in your app store"
                    className="size-full object-cover"
                  />
                </div>
              </div>

              {/* "or tap to open" divider */}
              <div className="flex items-center gap-2 w-full">
                <div className="flex-1 h-px bg-[#e4e4e7]" />
                <span className="text-sm font-medium text-black whitespace-nowrap">
                  or tap to open
                </span>
                <div className="flex-1 h-px bg-[#e4e4e7]" />
              </div>

              {/* Store buttons */}
              <div className="flex gap-3 w-full">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 flex items-center gap-2 p-3 rounded-xl bg-white border border-solid border-[#e4e4e7] hover:bg-[#fafafa] transition-colors no-underline"
                >
                  <span className="size-10 rounded-lg bg-[#f4f4f4] inline-flex items-center justify-center text-black shrink-0">
                    <Apple size={22} />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs font-medium text-[#444] leading-4">
                      Download on the
                    </span>
                    <span className="text-base font-semibold text-black leading-[22px]">
                      App Store
                    </span>
                  </span>
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 flex items-center gap-2 p-3 rounded-xl bg-white border border-solid border-[#e4e4e7] hover:bg-[#fafafa] transition-colors no-underline"
                >
                  <span className="size-10 rounded-lg bg-[#f4f4f4] inline-flex items-center justify-center text-black shrink-0">
                    <AndroidGlyph size={22} />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs font-medium text-[#444] leading-4">
                      Get it on
                    </span>
                    <span className="text-base font-semibold text-black leading-[22px]">
                      Google Play
                    </span>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {(sponsorLine || copyrightLine) && (
        <footer className="border-t border-[#e4e4e7] px-4 md:px-9 pt-6 pb-9">
          <p className="text-sm font-medium text-[#7e7f83] m-0">
            {sponsorLine}
            {sponsorLine && copyrightLine ? " · " : ""}
            {copyrightLine}
          </p>
        </footer>
      )}
    </div>
  );
};

export default DonePage;
